<template>
  <div :class="['input-wrapper', { 'input_error': error }, { 'input_disabled': disabled }]">
    <label
      v-if="label"
      :for="id"
      class="input__label"
    >
      {{ label }}
      <span v-if="required" class="input__label-required">*</span>
    </label>
    <div class="input__field-container">
      <slot name="hint" />

      <input
        :id="id"
        v-mask="inputMask"
        v-bind="$attrs"
        :value="value"
        :placeholder="inputPlaceholder || placeholder"
        autocomplete="off"
        class="input__field"
        @input="onInput($event.target.value)"
        @focus="error = false"
      >
    </div>
    <transition
      name="slide-from-top"
      @enter="errorEnter"
      @beforeLeave="errorBeforeLeave"
      @leave="errorLeave"
    >
      <span
        v-if="error && errorMsg"
        class="input__error-msg"
      >
        {{ errorMsg }}
      </span>
    </transition>
  </div>
</template>

<script>
  import { VueMaskDirective } from 'v-mask'

  export default {
    name: 'BaseInput',

    directives: { mask: VueMaskDirective },

    inheritAttrs: false,

    props: {
      value: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      errorMsg: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      mask: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      }
    },

    data () {
      return {
        error: null,
        marginBottom: 0
      }
    },

    computed: {
      id () {
        return `input-${this._uid}`
      },
      required () {
        return 'required' in this.$attrs && this.$attrs.required
      },
      inputMask () {
        switch (this.mask) {
        case 'tel': return this.$store.state.currentLang === 'uk' ? '+380-##-###-##-##' : '+###########'
        case 'vin': return 'NNNNNNNNNNNNNNNNN'
        default: return ''
        }
      },
      inputPlaceholder () {
        switch (this.mask) {
        case 'tel': return this.$store.state.currentLang === 'uk' ? '+380-__-___-__-__' : '+'
        default: return ''
        }
      }
    },

    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },

    mounted () {
      this.marginBottom = parseInt(getComputedStyle(this.$el).marginBottom)
    },

    methods: {
      onInput (value) {
        this.$emit('input', value)
      },

      errorEnter (el, done) {
        // first appear
        this.setErrorMargin(el, done, this.getErrorHeight(el))

        // second change innerText
        // watch on error
        this.unwatchError = this.$watch('error', () => {
          this.$nextTick(() => this.setErrorMargin(this.getErrorHeight(el)))
        })
      },

      errorBeforeLeave () {
        // unwatch error changes before error leave
        this.unwatchError()
      },

      errorLeave (el, done) {
        this.setErrorMargin(el, done, this.marginBottom)
      },

      setErrorMargin (el, done, marginBottom) {
        this.$el.style.marginBottom = `${marginBottom}px`
        done()
      },

      getErrorHeight (el) {
        let scrollHeight = (el && el.scrollHeight) || 0
        scrollHeight = scrollHeight > 20 ? 20 : scrollHeight
        return scrollHeight > this.marginBottom ? scrollHeight : this.marginBottom
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 28px;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    background-color: $c-white !important;
  }

  .input {
    &-wrapper {
      position: relative;
      min-width: 290px;
      max-width: 335px;
      padding-bottom: $padding-for-error;
    }

    &_disabled {
      pointer-events: none;
    }

    &__label {
      display: inline-block;
      padding-bottom: 8px;
      font-family: "FavoritC", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1.5;
      color: $c-black;

      &-required {
        background-color: $c-white;
        // color: $c-active-light;
      }
    }

    &__field {
      display: block;
      width: 100%;
      padding: 14px 18px;
      font-family: "FavoritC", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1.5;
      background-color: $c-white;
      border: 1px solid $c-gray-medium;
      border-radius: 0;
      outline: none;
      caret-color: $c-black;
      appearance: none;
      transition: 0.3s;

      @include mobile {
        font-size: 16px;
      }

      &-container {
        position: relative;
      }

      &:focus {
        border-color: $c-black;
      }
    }

    &__error-msg {
      position: absolute;
      top: 82%;
      left: 0;
      font-size: 14px;
      line-height: 20px;
      color: $c-active;
    }

    &_error {
      .input {
        &__field {
          border-color: $c-active;

          &-container {
            &::after {
              opacity: 1;
            }
          }
        }
      }
    }
  }
</style>
